import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RedactionRule, RedactionVersion, RedactionSetItem } from "ts/redaction";
import { Range } from "ts/range";
import { RadioOption } from "ts/radio";
import {
  changeAnalysisRedactionVersion,
  fetchAnalysisRedactionSets,
  fetchAnalysisRedactionVersions,
  fetchRedactionSets,
  fetchRedactionVersion,
  handleCreateRedactionVersion,
  handleEditRedactionVersion,
} from "./thunks";
import { RedactionDisplayMethod, RedactionRuleType } from "@explorance/mly-types";

type RedactionRuleReplaceValue = {
  index: number;
  replaceWith: string;
};

type RedactionRuleListNamePayload = {
  index: number;
  redactionSetId: number;
  isAlertType?: boolean;
  defaultReplacementValue?: string;
};

type RedactionRuleMethodPayload = {
  index: number;
  displayMethod: RedactionDisplayMethod;
  defaultReplacementValue: string;
};

type RedactionRuleAlertPayload = {
  index: number;
  alertRange: Range;
};

type RedactionModalState = {
  redactionVersionName: string;
  isModalOpen: boolean;
  redactionVersionId: number;
  newRedactionVersionId: number;
  redactionRules: RedactionRule[];
  isManualRedactionEnabled: boolean;
  redactionSets: RedactionSetItem[];
  redactionVersions: RedactionVersion[];
  selectedRedactionVersion: RadioOption;
  selectedVersionForDelete: RadioOption;
  loadingRedactionVersion: boolean;
};

const initialState: RedactionModalState = {
  redactionVersionName: "",
  isModalOpen: false,
  redactionVersionId: null,
  newRedactionVersionId: null,
  isManualRedactionEnabled: false,
  redactionRules: [],
  redactionVersions: [],
  selectedRedactionVersion: null,
  redactionSets: [],
  selectedVersionForDelete: null,
  loadingRedactionVersion: true,
};

const RedactionModalSlice = createSlice({
  name: "RedactionModalSlice",
  initialState,
  reducers: {
    setModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    toggleManualRedactionEnabled: (state) => {
      state.isManualRedactionEnabled = !state.isManualRedactionEnabled;
    },
    addRedactionRule: (state, action: PayloadAction<RedactionRule>) => {
      state.redactionRules.push(action.payload);
    },
    removeRedactionRule: (state, action: PayloadAction<number>) => {
      const redactionSetIndex = state.redactionSets.findIndex(
        (set) => set.id === state.redactionRules[action.payload].redactionSetId
      );
      if (redactionSetIndex !== -1) state.redactionSets[redactionSetIndex].isSelected = false;
      state.redactionRules.splice(action.payload, 1);
    },
    setRedactionRuleSet: (state, action: PayloadAction<RedactionRuleListNamePayload>) => {
      const oldIndex = state.redactionSets.findIndex(
        (set) => set.id === state.redactionRules[action.payload.index].redactionSetId
      );

      const setIndex = state.redactionSets.findIndex(
        (set) => set.id === action.payload.redactionSetId
      );

      if (oldIndex !== -1) state.redactionSets[oldIndex].isSelected = false;
      if (setIndex !== -1) state.redactionSets[setIndex].isSelected = true;

      state.redactionRules[action.payload.index] = {
        ruleType: action.payload.isAlertType ? RedactionRuleType.Alert : RedactionRuleType.Term,
        displayMethod: action.payload.isAlertType
          ? RedactionDisplayMethod.Total
          : RedactionDisplayMethod.Partial,

        replacementValue: action.payload.defaultReplacementValue,

        redactionSetId: action.payload.redactionSetId,
        ...(action.payload.isAlertType && {
          alertRange: { min: 90, max: 100 },
        }),
      };
    },
    setRedactionRuleMethod: (state, action: PayloadAction<RedactionRuleMethodPayload>) => {
      state.redactionRules[action.payload.index].displayMethod = action.payload.displayMethod;
      state.redactionRules = state.redactionRules.map((rule, index) => {
        return index === action.payload.index
          ? {
              ruleType: rule.ruleType,
              redactionSetId: rule.redactionSetId,
              alertRange: rule.alertRange,
              displayMethod: rule.displayMethod,
              ...(action.payload.displayMethod !== RedactionDisplayMethod.Remove && {
                replacementValue: action.payload.defaultReplacementValue,
              }),
            }
          : rule;
      });
    },
    setRedactionRuleAlertRange: (state, action: PayloadAction<RedactionRuleAlertPayload>) => {
      state.redactionRules[action.payload.index].alertRange = action.payload.alertRange;
    },
    setSelectedVersionForDelete: (state, action: PayloadAction<RadioOption>) => {
      state.selectedVersionForDelete = action.payload;
    },
    setSelectedRedactionVersion: (state, action: PayloadAction<RadioOption>) => {
      state.selectedRedactionVersion = action.payload;
    },
    setRedactionVersionId: (state, action: PayloadAction<number>) => {
      state.redactionVersionId = action.payload;
    },
    setNewRedactionVersionId: (state, action: PayloadAction<number>) => {
      state.newRedactionVersionId = action.payload;
    },
    setRedactionRuleReplaceWithValue: (state, action: PayloadAction<RedactionRuleReplaceValue>) => {
      state.redactionRules[action.payload.index].replacementValue = action.payload.replaceWith;
    },
    setRedactionVersionName: (state, action: PayloadAction<string>) => {
      state.redactionVersionName = action.payload;
    },
    clearState: (state) =>
      (state = {
        ...initialState,
        redactionVersions: state.redactionVersions,
        selectedRedactionVersion: state.selectedRedactionVersion,
      }),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAnalysisRedactionVersions.fulfilled, (state, action) => {
      state.redactionVersions = action.payload;
    });

    builder.addCase(fetchAnalysisRedactionSets.fulfilled, (state, action) => {
      state.redactionSets = action.payload.redactionSets;
    });

    builder.addCase(fetchRedactionSets.fulfilled, (state, action) => {
      state.redactionSets = action.payload.redactionSets;
    });

    builder.addCase(handleCreateRedactionVersion.fulfilled, (state) => {
      state.isModalOpen = false;
    });

    builder.addCase(changeAnalysisRedactionVersion.fulfilled, (state, action) => {
      state.selectedRedactionVersion = action.payload;
    });

    builder.addCase(handleEditRedactionVersion.fulfilled, (state) => {
      state.isModalOpen = false;
    });
    builder.addCase(fetchRedactionVersion.fulfilled, (state, action) => {
      state.redactionVersionName = action.payload.name;
      state.redactionRules = action.payload.rules;
      state.isManualRedactionEnabled = action.payload.includeManual;
      state.loadingRedactionVersion = false;
    });
    builder.addCase(fetchRedactionVersion.rejected, (state) => {
      state.loadingRedactionVersion = false;
      state.isModalOpen = false;
    });
  },
});

export const {
  setModalOpen,
  toggleManualRedactionEnabled,
  addRedactionRule,
  removeRedactionRule,
  setRedactionRuleSet,
  setRedactionRuleMethod,
  setRedactionVersionName,
  setNewRedactionVersionId,
  setRedactionVersionId,
  setSelectedVersionForDelete,
  setRedactionRuleReplaceWithValue,
  setSelectedRedactionVersion,
  setRedactionRuleAlertRange,
  clearState,
} = RedactionModalSlice.actions;

export default RedactionModalSlice.reducer;
